import React, { memo } from 'react';
import { Button, Text, ThemedImage, Callout } from 'core/components';
import { showErrorToast, showSuccessToast } from 'core/components/toast';
import kentikAiUser from 'app/assets/kentik-ai-user.svg';
import kentikAiUserDark from 'app/assets/kentik-ai-user-dark.svg';
import { inject } from 'mobx-react';
import { AGENT_TURN_STATUS_TYPES } from '@kentik/ui-shared/kaizen/constants';
import { MessageItem, AgentMessageBubble, MessageHeader, Timestamp } from '../KaizenChatSession.styles';
import StepComponent from './StepComponent';
import { getMainThreadSteps } from '../util/turnStepGroups';
import { isDeviceSpecificTurnError } from '../util/toolErrorUtils';
import { isInterruptedTurn, TURN_END_TEXT } from '../util/turnEndText';
import KaizenErrorMessage from './KaizenErrorMessage';

const stepSorter = (a, b) => (a.step_number || 0) - (b.step_number || 0);

// Memoized agent message component
const AgentMessage = memo(
  ({
    turn,
    isAsker,
    askerName,
    stepReasoningOpenState,
    onToggleReasoning,
    onSelectToolResult,
    onSendToExplorer,
    onShowAgentDataInSidebar,
    formatDate,
    isCopilotMode,
    inlineToolResults,
    sessionModel,
    isExistingChatLoading,
    $auth
  }) => {
    const sortedSteps = turn.steps?.slice().sort(stepSorter);

    const handleCopyLinkToTurn = () => {
      if (!turn?.id || !navigator?.clipboard?.writeText) {
        return;
      }

      const turnUrl = `${window.location.origin}${window.location.pathname}#kz-turn-${turn.id}`;
      navigator.clipboard
        .writeText(turnUrl)
        .then(() => {
          showSuccessToast('Link copied to clipboard');
        })
        .catch(() => {
          showErrorToast('Unable to copy link');
        });
    };

    return (
      <MessageItem key={`agent-${turn.id}`}>
        <AgentMessageBubble id={`kz-turn-${turn.id}`}>
          <MessageHeader>
            <ThemedImage src={kentikAiUser} darkSrc={kentikAiUserDark} width={24} height={24} />
            <Text fontSize={13} fontWeight="bold">
              Kentik AI
            </Text>
            <Timestamp muted fontSize={13} fontWeight="normal">
              {formatDate(turn.created_at)}
            </Timestamp>

            <Button
              className="answer-link-button"
              small
              minimal
              icon="link"
              onClick={handleCopyLinkToTurn}
              title="Copy link to this answer"
            />

            {$auth.isSudoer && (
              <Button
                small
                minimal
                icon="info-sign"
                onClick={() => onShowAgentDataInSidebar(turn, sortedSteps)}
                text="Show Data in Console"
              />
            )}
          </MessageHeader>

          {(() => {
            // Sub-agent steps are omitted here — their work lives in the sub-agent panel,
            // opened from the delegation tool call that summoned them.
            const mainSteps = getMainThreadSteps(sortedSteps);

            return mainSteps.map((step, stepIndex) => {
              const stepId = step.id || stepIndex;
              const isStepReasoningOpen = stepReasoningOpenState[stepId] ?? true;

              return (
                <StepComponent
                  key={`step-${stepId}`}
                  $auth={$auth}
                  turn={turn}
                  isAsker={isAsker}
                  askerName={askerName}
                  step={step}
                  stepIndex={stepIndex}
                  isStepReasoningOpen={isStepReasoningOpen}
                  onToggleReasoning={onToggleReasoning}
                  onSelectToolResult={onSelectToolResult}
                  onSendToExplorer={onSendToExplorer}
                  isCopilotMode={isCopilotMode}
                  inlineToolResults={inlineToolResults}
                  isExistingChatLoading={isExistingChatLoading}
                  sessionModel={sessionModel}
                />
              );
            });
          })()}

          {turn.processing_status === AGENT_TURN_STATUS_TYPES.CANCELLED && (
            <Callout intent="warning" icon="disable">
              <Text small>{TURN_END_TEXT[AGENT_TURN_STATUS_TYPES.CANCELLED]}</Text>
            </Callout>
          )}

          {turn.processing_status === AGENT_TURN_STATUS_TYPES.EXPIRED && (
            <Callout intent="warning" icon="time">
              <Text small>{TURN_END_TEXT[AGENT_TURN_STATUS_TYPES.EXPIRED]}</Text>
            </Callout>
          )}

          {isInterruptedTurn(turn) && (
            <Callout intent="warning" icon="offline">
              <Text small>{turn.error_message}</Text>
            </Callout>
          )}

          {turn.error_message && !isInterruptedTurn(turn) && (
            <Callout intent="danger">
              <Text small>
                Error:{' '}
                <KaizenErrorMessage
                  message={turn.error_message}
                  showMessage={isDeviceSpecificTurnError(turn)}
                  fallback="something went wrong"
                />
              </Text>
            </Callout>
          )}
        </AgentMessageBubble>
      </MessageItem>
    );
  }
);

AgentMessage.displayName = 'AgentMessage';

export default inject('$auth')(AgentMessage);
